import { CalendarRange, Coins } from "lucide-react"
import { useEffect } from "react"
import { useNavigate, useParams } from "react-router"

import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Conceptos } from "@/paginas/interno/nomina/Conceptos"
import { Periodos } from "@/paginas/interno/nomina/Periodos"

export const VISTAS = [
  { valor: "periodos", etiqueta: "Períodos", icono: CalendarRange, permiso: "nomina:ver" },
  { valor: "conceptos", etiqueta: "Conceptos", icono: Coins, permiso: "nomina:ver" },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

/**
 * La nómina FINANCIERA: lo que cuesta la gente de la casa, dentro de «Administración».
 *
 * ⚠ **Los contratos NO están aquí: viven en «Talento humano».** La agencia parte la
 * relación con el empleado en dos —lo laboral y lo financiero— y cada mitad la lleva
 * su departamento: quién está contratado, con qué sueldo y desde cuándo es de talento
 * humano; qué se le paga este mes y con qué conceptos, de administración. Antes era
 * una sola entrada del menú, «Sueldos y salarios», con las tres vistas juntas.
 *
 * ⚠ **Esto no paga a nadie.** Lo que produce son órdenes de pago en BORRADOR, que
 * salen por Tesorería con las tres firmas de siempre. El dinero tiene un solo camino.
 *
 * ⚠ **Es una segunda fila de pestañas, y la vista va en la URL**
 * (`/administracion/nomina/periodos`): el parámetro es `sub`, porque `vista` ya lo
 * gasta «Administración». Ni título ni frase propios — los pone su cabecera.
 *
 * ⚠ **Períodos va primero.** Es lo que se abre cada quincena; los conceptos se tocan
 * cuando cambia una bonificación, que es dos veces al año.
 */
export function Nomina() {
  const usuario = usarSesion((e) => e.usuario)
  const { sub: enRuta } = useParams()
  const navegar = useNavigate()

  const visibles = VISTAS.filter((v) => tienePermiso(usuario, v.permiso))
  const vista: Vista | undefined =
    visibles.find((v) => v.valor === enRuta)?.valor ?? visibles[0]?.valor

  useEffect(() => {
    if (vista !== undefined && enRuta !== vista) {
      navegar(`/administracion/nomina/${vista}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  if (vista === undefined) {
    return (
      <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
        No tienes acceso a ninguna de sus vistas.
      </p>
    )
  }

  return (
    <div>
      <Pestanas
        pestanas={visibles}
        activa={vista}
        onCambiar={(v) => navegar(`/administracion/nomina/${v}`, { replace: true })}
        etiqueta="Vistas de nómina"
        idPanel="panel-nomina"
        prefijoId="pestana-nomina"
      />
      <div
        id="panel-nomina"
        role="tabpanel"
        aria-labelledby={`pestana-nomina-${vista}`}
        className="mt-4"
      >
        {vista === "periodos" && <Periodos />}
        {vista === "conceptos" && <Conceptos />}
      </div>
    </div>
  )
}
